import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "./api";
import { useAuthStore } from "./store";
import type { LoginInput } from "./schemas";

export const useWorkspaces = () => {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ["auth", "workspaces"],
    queryFn: authApi.workspaces,
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
};

export const useSwitchWorkspace = () =>
  useMutation({
    mutationFn: (tenantId: string) => authApi.switchWorkspace(tenantId),
    onSuccess: (data) => {
      if (data.user?.tenant?.id) {
        localStorage.setItem("tenantId", data.user.tenant.id);
      }
      // Full reload on purpose: guarantees no cached query data from the
      // previous workspace can leak into the new one.
      window.location.href = "/";
    },
  });

export const useCurrentUser = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const markChecked = useAuthStore((s) => s.markSessionChecked);

  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        const user = await authApi.me();
        setUser(user);
        return user;
      } catch (e) {
        markChecked();
        throw e;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
};

export const useLogin = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (data) => {
      // Drop every cached query from the previous session so a different
      // user/tenant never sees stale header, menus or dashboard data.
      queryClient.clear();
      if (data?.user) setUser(data.user);
    },
  });
};

export const useLogout = () => {
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
};
